import { requiredWhenAsked } from '../completion';
import { yesNo } from './fields';
import {
  dateRules,
  endDateRules,
  nameRules,
  phoneRules,
  salaryRules,
} from './validation';

const EMPLOYER_FIELDS = [
  'Position',
  'AnnualSalary',
  'StartDate',
  'EmployerName',
  'EmployerPhone',
  'SupervisorName',
  'StreetAddress',
  'City',
  'State',
  'ZipCode',
];
const UNEMPLOYED_PREFIXES = ['currentJob', 'secondJob'];

// Choosing Not Employed empties the current and second employers.
const clearEmployers = (notEmployedStatusId) =>
  UNEMPLOYED_PREFIXES.flatMap((prefix) =>
    EMPLOYER_FIELDS.map((field) => ({
      type: 'updateField',
      target: `${prefix}${field}`,
      value: '',
      when: { eq: notEmployedStatusId },
    })),
  );

const buildEmploymentInformation = ({
  isLocked,
  notEmployedStatusId,
  employedStatusId,
}) => ({
  name: 'employmentInformation',
  title: 'EMPLOYMENT INFORMATION',
  titleVariant: 'subsection',
  divider: true,
  container: 'default',
  disabled: isLocked,
  fields: [
    {
      variant: 'radioGroup',
      name: 'employmentStatus',
      label: 'Status',
      sx: { flexDirection: 'row' },
      rules: {
        required: requiredWhenAsked('employmentStatus', { employedStatusId }),
      },
      ...(notEmployedStatusId && {
        changeActions: clearEmployers(notEmployedStatusId),
      }),
      GridProps: { xs: 12 },
    },
  ],
});

const employer = ({
  prefix,
  title,
  startLabel,
  hasEndDate,
  disabled,
  watchFields,
  requiredOptions,
}) =>
  withRequired(requiredOptions, {
    name: `${prefix}Employer`,
    title,
    ...(disabled !== undefined && { disabled }),
    ...(watchFields && { watchFields }),
    titleVariant: 'subsection',
    divider: true,
    container: 'default',
    fields: [
      {
        variant: 'text',
        name: `${prefix}Position`,
        label: 'Position',
        GridProps: { xs: 12, sm: 3 },
      },
      {
        variant: 'currency',
        name: `${prefix}AnnualSalary`,
        label: 'Annual Income',
        rules: salaryRules,
        GridProps: { xs: 12, sm: 3 },
      },
      {
        variant: 'date',
        name: `${prefix}StartDate`,
        label: startLabel,
        rules: dateRules,
        GridProps: { xs: 12, sm: 3 },
      },
      ...(hasEndDate
        ? [
            {
              variant: 'date',
              name: `${prefix}EndDate`,
              label: 'To',
              rules: endDateRules(`${prefix}StartDate`),
              GridProps: { xs: 12, sm: 3 },
            },
          ]
        : [{ variant: 'flexSpacer' }]),
      {
        variant: 'text',
        name: `${prefix}EmployerName`,
        label: 'Employer Name',
        rules: nameRules,
        GridProps: { xs: 12, sm: 4 },
      },
      {
        variant: 'phone',
        name: `${prefix}EmployerPhone`,
        label: 'Employer Phone Number',
        rules: phoneRules,
        GridProps: { xs: 12, sm: 4 },
      },
      {
        variant: 'text',
        name: `${prefix}SupervisorName`,
        label: 'Supervisor Name',
        rules: nameRules,
        GridProps: { xs: 12, sm: 4 },
      },
      {
        variant: 'text',
        name: `${prefix}StreetAddress`,
        label: 'Employer Street Address',
        GridProps: { xs: 12, sm: 4 },
      },
      {
        variant: 'text',
        name: `${prefix}City`,
        label: 'City',
        GridProps: { xs: 12, sm: 3 },
      },
      {
        variant: 'singleSelect',
        name: `${prefix}State`,
        label: 'State/Province',
        GridProps: { xs: 12, sm: 3 },
      },
      {
        variant: 'text',
        name: `${prefix}ZipCode`,
        label: 'Zip Code/Postal Code',
        GridProps: { xs: 12, sm: 2 },
      },
    ],
  });

// Only the current employer is required, and only for an employed applicant; the rule is re-read
// when the status changes.
function withRequired(requiredOptions, section) {
  if (!requiredOptions) return section;
  return {
    ...section,
    fields: section.fields.map((field) =>
      field.name
        ? {
            ...field,
            rules: {
              ...field.rules,
              required: requiredWhenAsked(field.name, requiredOptions),
            },
            watchFields: ['employmentStatus'],
          }
        : field,
    ),
  };
}

// The current and second employers do not apply to an unemployed applicant.
const unemployedRule =
  ({ isLocked, notEmployedStatusId }) =>
  ({ getValues }) =>
    isLocked ||
    (Boolean(notEmployedStatusId) &&
      getValues('employmentStatus') === notEmployedStatusId);

export const buildCurrentEmployer = (options) =>
  employer({
    prefix: 'currentJob',
    title: "APPLICANT'S CURRENT EMPLOYER",
    startLabel: 'Start Date',
    hasEndDate: false,
    disabled: unemployedRule(options),
    watchFields: ['employmentStatus'],
    requiredOptions: { employedStatusId: options.employedStatusId },
  });

export const buildSecondEmployer = (options) =>
  employer({
    prefix: 'secondJob',
    title: "APPLICANT'S 2ND CURRENT EMPLOYER (IF APPLICABLE)",
    startLabel: 'Start Date',
    hasEndDate: false,
    disabled: unemployedRule(options),
    watchFields: ['employmentStatus'],
  });

export const buildPreviousEmployer = ({ isLocked }) =>
  employer({
    prefix: 'previousJob',
    title: "APPLICANT'S PREVIOUS EMPLOYER",
    startLabel: 'From',
    hasEndDate: true,
    // Unlike the other two, the previous employer does not depend on the status.
    disabled: isLocked,
  });

export const buildEmploymentChange = ({ isLocked, employedStatusId }) => ({
  name: 'employmentChange',
  container: 'default',
  disabled: isLocked,
  fields: [
    {
      ...yesNo(
        'anticipateChangeEmployer',
        'Do you anticipate a change with your current employer situation? ' +
          'Note: you must inform us if your employment situation changes prior to the new lease start date.',
      ),
      rules: {
        required: requiredWhenAsked('anticipateChangeEmployer', {
          employedStatusId,
        }),
      },
    },
  ],
});

export default buildEmploymentInformation;
